/**
 * Procedural stocker rack layout (Y-up, bottom-center origin).
 *
 * Mirrors production `getStockerSlots` capacity:
 * - 7 vertical levels
 * - 2 depth rows (front +Z toward OHT / interbay ports, back −Z)
 * - Left + right bay columns around the center crane aisle
 *
 * The crane aisle is the intrabay path (no separate back-side load ports).
 */
import {
  STOCKER_RACK_LEVELS,
  resolveStockerBayCount,
} from '../../../../utils/stockerCapacity';
import { resolveStockerDimensions } from './stockerTypes';
import type { StockerFoupSlotState } from './stockerTypes';

export {
  STOCKER_RACK_LEVELS,
  resolveStockerBayCount,
  resolveStockerSlotCapacity,
} from '../../../../utils/stockerCapacity';

export type StockerRackSlot = {
  key: string;
  index: number;
  /** Bottom-center of the slot volume. */
  position: [number, number, number];
  w: number;
  h: number;
  d: number;
  /** +1 = front (OHT face / +Z), −1 = back (−Z). */
  depthSign: -1 | 1;
};

export type StockerRackLayout = {
  levels: number;
  leftCols: number;
  rightCols: number;
  depthRows: number;
  totalSlots: number;
  aisleHalfWidth: number;
  slots: StockerRackSlot[];
  slotW: number;
  slotH: number;
  slotD: number;
};

/** Bay count → columns left / right of the crane aisle (matches Stocker3DModel). */
export function resolveBayColumns(bayCount?: string | number): {
  leftCols: number;
  rightCols: number;
} {
  // Each X column has a front and back bay, hence bayCount / 2 columns.
  const sideColumns = Math.max(1, Math.ceil(resolveStockerBayCount(bayCount) / 2));
  return {
    leftCols: Math.ceil(sideColumns / 2),
    rightCols: Math.floor(sideColumns / 2),
  };
}

/**
 * Build rack slots filling bottom→top, front then back, left then right —
 * same occupancy fill order the transfer anim uses (index 0..N-1).
 */
export type StockerRackInterior = {
  /** Y of the rack floor (bottom of lowest shelf). */
  bottom: number;
  /** Y of the rack ceiling (top of highest shelf volume). */
  top: number;
};

export function buildStockerRackLayout(
  width: number,
  height: number,
  depth: number,
  bayCount?: string | number,
  interior?: StockerRackInterior
): StockerRackLayout {
  const levels = STOCKER_RACK_LEVELS;
  const { leftCols, rightCols } = resolveBayColumns(bayCount);
  const depthRows = 2;

  const marginX = width * 0.06;
  const rackBottom = interior?.bottom ?? height * 0.06;
  const rackTop = interior?.top ?? height - height * 0.06;
  const marginY = rackBottom;
  const aisleHalfZ = Math.max(depth * 0.1, Math.min(depth * 0.18, 0.55));
  const mastClearX = Math.max(width * 0.07, 0.55);
  const usableH = Math.max(0.5, rackTop - rackBottom - height * 0.015);
  // Each depth shelf sits between crane aisle and outer skin.
  const shelfDepth = Math.max(0.4, (depth / 2 - aisleHalfZ) * 0.9);
  const sideSpan = Math.max(0.5, width / 2 - marginX - mastClearX);

  const gapY = usableH * 0.14;
  const gapX = sideSpan * 0.08;
  const maxSideCols = Math.max(leftCols, rightCols, 1);
  const slotW = (sideSpan - gapX * Math.max(0, maxSideCols - 1)) / maxSideCols;
  const slotH = (usableH - gapY * (levels - 1)) / levels;
  const slotD = shelfDepth;

  const slots: StockerRackSlot[] = [];
  let index = 0;

  const xForLeft = (b: number) => -mastClearX - slotW / 2 - b * (slotW + gapX);
  const xForRight = (b: number) => mastClearX + slotW / 2 + b * (slotW + gapX);

  for (let level = 0; level < levels; level++) {
    const y = marginY + level * (slotH + gapY);
    for (const depthSign of [1, -1] as const) {
      const z = depthSign * (aisleHalfZ + shelfDepth * 0.5);
      for (let b = 0; b < leftCols; b++) {
        slots.push({
          key: `lv${level}-d${depthSign}-L${b}`,
          index: index++,
          position: [xForLeft(b), y, z],
          w: slotW,
          h: slotH,
          d: slotD,
          depthSign,
        });
      }
      for (let b = 0; b < rightCols; b++) {
        slots.push({
          key: `lv${level}-d${depthSign}-R${b}`,
          index: index++,
          position: [xForRight(b), y, z],
          w: slotW,
          h: slotH,
          d: slotD,
          depthSign,
        });
      }
    }
  }

  return {
    levels,
    leftCols,
    rightCols,
    depthRows,
    totalSlots: slots.length,
    aisleHalfWidth: aisleHalfZ,
    slots,
    slotW,
    slotH,
    slotD,
  };
}

/**
 * Rack slot FOUP poses for transfer animation — same layout as Stocker3D shelves.
 */
export function buildStockerTransferSlotPositions(
  width: number,
  height: number,
  depth: number,
  bayCount?: string | number
): Array<[number, number, number]> {
  const dims = resolveStockerDimensions(width, height, depth);
  const rack = buildStockerRackLayout(width, height, depth, bayCount, {
    bottom: dims.rackBottom,
    top: dims.rackTop,
  });
  const trayH = rack.slotH * 0.12;
  return rackLayoutToFoupSlotStates(rack, trayH).map(
    (s) => s.position as [number, number, number]
  );
}

/** Map procedural rack layout → Stocker3D slot states (shelf deck center positions). */
export function rackLayoutToFoupSlotStates(
  rack: StockerRackLayout,
  deckT: number
): StockerFoupSlotState[] {
  return rack.slots.map((s) => {
    const levelMatch = /^lv(\d+)/.exec(s.key);
    const level = levelMatch ? Number(levelMatch[1]) : 0;
    return {
      slotId: s.index,
      level,
      position: [s.position[0], s.position[1] + deckT * 0.5, s.position[2]],
      occupied: false,
    };
  });
}
